import { ICatalog, ICategoryOrProduct, isCategory } from "shared/hubrise/models"
import { DisplayMode, EditPage, editPath, GridPage, gridPath, imagePath, previewPath } from "shared/utils/paths"

import { allOptions } from "../utils/Option"
import { allSkus } from "../utils/Sku"

import { LastVisits } from "./LastVisits"

export class CatalogNavigation {
  private readonly visits: LastVisits

  constructor(private catalog: ICatalog) {
    this.visits = new LastVisits(this.catalog)
  }

  visit(...visitArgs: Parameters<LastVisits["visit"]>): void {
    this.visits.visit(...visitArgs)
  }

  get lastCategoryOrProduct(): ICategoryOrProduct | undefined {
    return this.visits.category || this.visits.product || undefined
  }

  lastVisitedPath(displayMode: DisplayMode): string {
    switch (displayMode) {
      case "edit":
        return this.editLastVisitedPath()
      case "grid":
        return this.gridLastVisitedPath()
      case "preview":
        return this.previewPath("all")
    }
  }

  editLastVisitedPath(page?: EditPage): string {
    if (!page) {
      page = this._editLastVisitedPage || "products"
    }

    switch (page) {
      case "products":
        if (this.visits.category) {
          return this.editPath("category", this.visits.category.id)
        } else if (this.visits.product) {
          return this.editPath("product", this.visits.product.id)
        }
        return this.editPath("products")
      case "option_lists":
        if (this.visits.optionList) return this.editPath("option_list", this.visits.optionList.id)
        return this.editPath("option_lists")
      case "deals":
        if (this.visits.deal) return this.editPath("deal", this.visits.deal.id)
        return this.editPath("deals")
      case "discounts":
        if (this.visits.discount) return this.editPath("discount", this.visits.discount.id)
        return this.editPath("discounts")
      case "charges":
        if (this.visits.charge) return this.editPath("charge", this.visits.charge.id)
        return this.editPath("charges")
    }
  }

  editNextPath(page: EditPage): string {
    switch (page) {
      case "products": {
        const nextCategoryOrProduct = this.visits.nextCategoryOrProduct
        if (nextCategoryOrProduct) {
          return isCategory(nextCategoryOrProduct)
            ? this.editPath("category", nextCategoryOrProduct.id)
            : this.editPath("product", nextCategoryOrProduct.id)
        }
        return this.editPath("products")
      }
      case "option_lists": {
        const nextOptionList = this.visits.nextOptionList
        if (nextOptionList) {
          return this.editPath("option_list", nextOptionList.id)
        }
        return this.editPath("option_lists")
      }
      case "deals": {
        const nextDeal = this.visits.nextDeal
        if (nextDeal) {
          return this.editPath("deal", nextDeal.id)
        }
        return this.editPath("deals")
      }
      case "discounts": {
        const nextDiscount = this.visits.nextDiscount
        if (nextDiscount) {
          return this.editPath("discount", nextDiscount.id)
        }
        return this.editPath("discounts")
      }
      case "charges": {
        const nextCharge = this.visits.nextCharge
        if (nextCharge) {
          return this.editPath("charge", nextCharge.id)
        }
        return this.editPath("charges")
      }
    }
  }

  private get _editLastVisitedPage(): EditPage | null {
    if (this.visits.isLastVisitCategory || this.visits.isLastVisitProduct) {
      return "products"
    } else if (this.visits.isLastVisitOptionList) {
      return "option_lists"
    } else if (this.visits.isLastVisitDeal) {
      return "deals"
    } else if (this.visits.isLastVisitDiscount) {
      return "discounts"
    } else if (this.visits.isLastVisitCharge) {
      return "charges"
    } else {
      return null
    }
  }

  gridLastVisitedPath(page?: GridPage): string {
    if (!page) {
      page = this._gridLastVisitedPage || "skus"
    }

    switch (page) {
      case "skus":
        if (this.visits.sku) {
          return this.gridPath("sku", this.visits.sku.id)
        } else if (this.visits.category) {
          const firstSkuOfCategory = allSkus(this.catalog).find(
            ({ product }) => product.categoryId === this.visits.category!.id,
          )?.sku // May have been deleted
          return firstSkuOfCategory ? this.gridPath("sku", firstSkuOfCategory.id) : this.gridPath("skus")
        }
        return this.gridPath("skus")
      case "options":
        if (this.visits.option) {
          return this.gridPath("option", this.visits.option.id)
        } else if (this.visits.optionList) {
          const firstOptionOfList = allOptions(this.catalog).find(
            ({ optionList }) => optionList.id === this.visits.optionList!.id,
          )?.option // May have been deleted
          return firstOptionOfList ? this.gridPath("option", firstOptionOfList.id) : this.gridPath("options")
        }
        return this.gridPath("options")
      case "deals":
        if (this.visits.deal) {
          return this.gridPath("deal", this.visits.deal.id)
        }
        return this.gridPath("deals")
    }
  }

  private get _gridLastVisitedPage(): GridPage | null {
    if (this.visits.isLastVisitCategory || this.visits.isLastVisitSku) {
      return "skus"
    } else if (this.visits.isLastVisitOption) {
      return "options"
    } else if (this.visits.isLastVisitDeal) {
      return "deals"
    } else {
      return null
    }
  }

  // Convenience methods

  editPath(...params: OmitCatalogId<typeof editPath>): string {
    return editPath(this.catalog.hrId, ...params)
  }

  gridPath(...params: OmitCatalogId<typeof gridPath>): string {
    return gridPath(this.catalog.hrId, ...params)
  }

  previewPath(...params: OmitCatalogId<typeof previewPath>): string {
    return previewPath(this.catalog.hrId, ...params)
  }

  imagePath(...params: OmitCatalogId<typeof imagePath>): string {
    return imagePath(this.catalog.hrId, ...params)
  }

  // End convenience methods
}

type OmitCatalogId<T extends (...args: any) => any> = T extends (catalogId: string, ...args: infer Rest) => any
  ? Rest
  : never
